import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import PreviousResults from "@/pages/PreviousResults";
import Predictions from "@/pages/Predictions";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminManageResults from "@/pages/AdminManageResults";
import AdminChangePassword from "@/pages/AdminChangePassword";
import AdminResultForm from "@/pages/AdminResultForm";
import AdminPreviousResults from "@/pages/AdminPreviousResults";
import AdminCommonNumber from "@/pages/AdminCommonNumber";
import NotFound from "@/pages/NotFound";
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Disclaimer from "@/pages/Disclaimer";

import GameResultToday from "@/pages/GameResultToday";
import GameCommonNumber from "@/pages/GameCommonNumber";
import GamePreviousResult from "@/pages/GamePreviousResult";
import { Navigate } from "react-router-dom";
import {
    SEO_MARKETS,
    resultTodayPath,
    commonNumberPath,
    previousResultPath,
} from "@/lib/seoMarkets";

function Shell({ children, showChrome = true }) {
    return (
        <div className="App min-h-screen flex flex-col">
            {showChrome && <Navbar />}
            <main className="flex-1">{children}</main>
            {showChrome && <Footer />}
        </div>
    );
}

// Register both no-trailing-slash and trailing-slash variants for each SEO page
// so users landing from Google (either form) all hit the same component.
const seoRoutes = SEO_MARKETS.flatMap((m) => [
    {
        path: resultTodayPath(m.urlPrefix),
        el: <GameResultToday marketKey={m.key} />,
    },
    {
        path: commonNumberPath(m.urlPrefix),
        el: <GameCommonNumber marketKey={m.key} />,
    },
    {
        path: previousResultPath(m.urlPrefix),
        el: <GamePreviousResult marketKey={m.key} />,
    },
]);

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="top-right" richColors />
                <Routes>
                    <Route path="/" element={<Shell><Home /></Shell>} />
                    <Route path="/common-number" element={<Shell><Predictions type="common_number" /></Shell>} />
                    <Route path="/dream-number" element={<Shell><Predictions type="dream_number" /></Shell>} />
                    <Route path="/hit-guti" element={<Shell><Predictions type="hit_guti" /></Shell>} />
                    <Route path="/previous-results" element={<Shell><PreviousResults /></Shell>} />
                    <Route path="/about-us" element={<Shell><AboutUs /></Shell>} />
                    <Route path="/contact-us" element={<Shell><ContactUs /></Shell>} />
                    <Route path="/privacy-policy" element={<Shell><PrivacyPolicy /></Shell>} />
                    <Route path="/terms-and-conditions" element={<Shell><Terms /></Shell>} />
                    <Route path="/disclaimer" element={<Shell><Disclaimer /></Shell>} />

                    {/* --- 24 SEO landing pages (8 games × 3 page types) --- */}
                    {seoRoutes.flatMap(({ path, el }) => [
                        <Route key={path} path={path} element={<Shell>{el}</Shell>} />,
                        // Trailing-slash variant so /juwai-teer-result-today/ also resolves
                        <Route key={`${path}/`} path={`${path}/`} element={<Shell>{el}</Shell>} />,
                    ])}

                    {/* --- 301-style redirects for old `morning-teer-*` URLs (now `morning-sunday-teer-*`) --- */}
                    <Route path="/morning-teer-result-today" element={<Navigate to="/morning-sunday-teer-result-today" replace />} />
                    <Route path="/morning-teer-common-number" element={<Navigate to="/morning-sunday-teer-common-number" replace />} />
                    <Route path="/morning-teer-previous-result" element={<Navigate to="/morning-sunday-teer-previous-result" replace />} />
                    <Route path="/morning-teer-result-today/" element={<Navigate to="/morning-sunday-teer-result-today" replace />} />
                    <Route path="/morning-teer-common-number/" element={<Navigate to="/morning-sunday-teer-common-number" replace />} />
                    <Route path="/morning-teer-previous-result/" element={<Navigate to="/morning-sunday-teer-previous-result" replace />} />

                    <Route path="/admin/login" element={<Shell showChrome={false}><AdminLogin /></Shell>} />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute>
                                <AdminDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/results"
                        element={
                            <ProtectedRoute>
                                <AdminManageResults />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/change-password"
                        element={
                            <ProtectedRoute>
                                <AdminChangePassword />
                            </ProtectedRoute>
                        }
                    />
                    {/* Legacy per-market admin routes — kept for backward compat but no longer linked from dashboard */}
                    <Route
                        path="/admin/results/:marketKey"
                        element={
                            <ProtectedRoute>
                                <AdminResultForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/previous/:marketKey"
                        element={
                            <ProtectedRoute>
                                <AdminPreviousResults />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/common/:marketKey"
                        element={
                            <ProtectedRoute>
                                <AdminCommonNumber />
                            </ProtectedRoute>
                        }
                    />

                    {/* --- Catch-all 404 page --- */}
                    <Route path="*" element={<Shell><NotFound /></Shell>} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
